/**
 * Checks if a character is CJK (Chinese, Japanese, Korean)
 */
const isCJKCharacter = (char: string): boolean => {
    const code = char.charCodeAt(0);
    return (
        (code >= 0x4e00 && code <= 0x9fff) || // CJK Unified Ideographs
        (code >= 0x3400 && code <= 0x4dbf) || // CJK Extension A
        (code >= 0x20000 && code <= 0x2a6df) || // CJK Extension B
        (code >= 0xf900 && code <= 0xfaff) || // CJK Compatibility Ideographs
        (code >= 0x3040 && code <= 0x309f) || // Hiragana
        (code >= 0x30a0 && code <= 0x30ff) || // Katakana
        (code >= 0xac00 && code <= 0xd7af) || // Hangul
        (code >= 0x3000 && code <= 0x303e) || // CJK Symbols and Punctuation, excluding Ideographic Half Fill Space (303f)
        (code >= 0xff01 && code <= 0xff60) || // Halfwidth and Fullwidth Forms (Fullwidth characters)
        (code >= 0xffe0 && code <= 0xffe6) //  Halfwidth and Fullwidth Forms (Fullwidth symbols and currency signs)
    );
};

/**
 * Estimates the width of a character
 */
const getCharWidth = (char: string, fontSize: number): number => {
    if (isCJKCharacter(char)) {
        return fontSize * 1.15; // CJK characters need more space with padding
    }
    // Latin characters, numbers, and punctuation
    if (/[A-Z]/.test(char)) return fontSize * 0.75; // Uppercase
    if (/[a-z]/.test(char)) return fontSize * 0.6; // Lowercase
    if (/[0-9]/.test(char)) return fontSize * 0.65; // Numbers
    if (/[\s]/.test(char)) return fontSize * 0.35; // Space
    return fontSize * 0.6; // Default for punctuation and others
};

/**
 * Estimates the height needed for a text block based on its content and styling
 * This function simulates word wrapping to calculate the required height
 * @param text - The text content
 * @param fontSize - Font size in pixels
 * @param width - Available width in pixels
 * @param lineHeight - Line height multiplier (default: 1.5)
 * @param padding - Additional padding to add (default: 16)
 * @returns Estimated height in pixels
 */
export const calculateTextHeight = (text: string, fontSize: number, width: number, lineHeight: number = 1.5, padding: number = 16): number => {
    if (!text) return fontSize * lineHeight + padding;

    // Account for internal padding that might exist in the text component
    const effectiveWidth = width * 0.9; // Use 90% of width to be conservative

    let lines = 1;
    let currentLineWidth = 0;

    // Split by characters but keep track of words for Latin text
    const chars = text.split('');
    let currentWord = '';
    let currentWordWidth = 0;

    for (let i = 0; i < chars.length; i++) {
        const char = chars[i];
        if (!char) continue; // Skip undefined characters

        const charWidth = getCharWidth(char, fontSize);

        // For CJK characters, they can break anywhere
        if (isCJKCharacter(char)) {
            // Finish current word if any
            if (currentWord) {
                if (currentLineWidth + currentWordWidth > effectiveWidth) {
                    lines++;
                    currentLineWidth = currentWordWidth + charWidth;
                } else {
                    currentLineWidth += currentWordWidth + charWidth;
                }
                currentWord = '';
                currentWordWidth = 0;
            } else {
                // Add CJK character
                if (currentLineWidth + charWidth > effectiveWidth) {
                    lines++;
                    currentLineWidth = charWidth;
                } else {
                    currentLineWidth += charWidth;
                }
            }
        } else if (/\s/.test(char)) {
            // Space - finish current word
            if (currentWord) {
                if (currentLineWidth + currentWordWidth + charWidth > effectiveWidth) {
                    lines++;
                    currentLineWidth = 0;
                } else {
                    currentLineWidth += currentWordWidth + charWidth;
                }
                currentWord = '';
                currentWordWidth = 0;
            } else {
                currentLineWidth += charWidth;
            }
        } else {
            // Building a word (Latin characters, numbers, etc.)
            currentWord += char;
            currentWordWidth += charWidth;
        }
    }

    // Handle remaining word
    if (currentWord) {
        if (currentLineWidth + currentWordWidth > effectiveWidth) {
            lines++;
        }
    }

    // Use a more generous line height calculation
    const calculatedHeight = lines * fontSize * lineHeight + padding;

    // Add small buffer for safety (10% more height to account for rendering differences)
    return Math.max(calculatedHeight * 1, fontSize * lineHeight + padding) + 10;
};
